import { Position, UserDetails } from '../services/org-board-builder.service';
import { OrbBoardBaseComponent } from '../org-board-base-component';
import { OrgBoardService } from '../services/org-board-service';
import { ProfileColorService } from '../services/profile-color.service';
import { CommonModule, NgStyle } from '@angular/common';
import { MaterialModule } from '../../material.module';
import { Component, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { NormalizePipe } from '../format-pipe';
import { ECONode, Organization, OrgHat, OrgUser } from '@newgenus/common';
import { RightSideBarService } from '../../../services/right-sidebar.service';

/** Hat info resolved for this user */
interface UserHatInfo {
    key: string;
    name: string;
    description: string;
    positionNames: string[];
    securityGroupNames: string[];
    userCount: number;
    /** Positionless hat that grants org-wide elevated permissions. */
    isSpecialRights?: boolean;
}

/** Position info resolved for this user */
interface UserPositionInfo {
    partKey: string;
    name: string;
    partType: string;
    hatName: string;
}

/** SG info resolved for this user */
interface UserSecurityGroupInfo {
    key: string;
    name: string;
    description?: string;
    features: string[];
    featureDetails: Record<string, { permissions?: string[] }>;
    featureDescriptions: Record<string, string>;
    permissionDescriptions: Record<string, Record<string, string>>;
    viaHats: string[];
}

/** Policy info resolved for this user */
interface UserPolicyInfo {
    key: string;
    name: string;
    description?: string;
    category?: string;
    status?: string;
    hatName: string;
}

@Component({
    selector: 'shared-sidebar-user',
    imports: [CommonModule, MaterialModule, NgStyle, NormalizePipe],
    template: `
        <!-- ═══ User Header Card (sticky) ═══ -->
        <div class="sticky-header-wrapper">
            <mat-card class="position-header-card">
                <mat-card-header>
                    <div mat-card-avatar class="user-avatar-header">
                        <img [src]="getUserAvatarUrl()" [alt]="userName" />
                    </div>
                    <mat-card-title>{{ userName | titlecase }}</mat-card-title>
                    <mat-card-subtitle>
                        <div>{{ userEmail || 'No email' }}</div>
                        <span
                            *ngIf="hasSpecialRights"
                            class="sr-header-chip"
                            matTooltip="This staff member holds Special Rights hats granting org-wide elevated permissions"
                        >
                            <mat-icon>shield</mat-icon> Special Rights
                        </span>
                    </mat-card-subtitle>
                </mat-card-header>

                <mat-card-content class="pt-2">
                    <div class="quick-stats">
                        <div class="quick-stat">
                            <strong>{{ userHats.length }}</strong> <small>Hats</small>
                        </div>
                        <div class="quick-stat">
                            <strong>{{ userPositions.length }}</strong> <small>Org Parts</small>
                        </div>
                        <div class="quick-stat">
                            <strong>{{ userSecurityGroups.length }}</strong> <small>SGs</small>
                        </div>
                        <div class="quick-stat">
                            <strong>{{ userPolicies.length }}</strong> <small>Policies</small>
                        </div>
                    </div>

                    <!-- Pending banner: shown when user has any pending hat assignments -->
                    <div
                        *ngIf="pendingHats.length > 0 || pendingPositions.length > 0"
                        class="pending-banner"
                        matTooltip="This staff member is awaiting approval on the items below. They will not have these rights until approved."
                    >
                        <div class="pending-banner-header">
                            <mat-icon class="pending-banner-icon">hourglass_empty</mat-icon>
                            <span class="pending-banner-title">Pending Approval</span>
                        </div>
                        <div class="pending-banner-body">
                            <span *ngIf="pendingHats.length > 0" class="pending-banner-line"
                                >+{{ pendingHats.length }} hat{{ pendingHats.length > 1 ? 's' : '' }}</span
                            >
                            <span *ngIf="pendingPositions.length > 0" class="pending-banner-line"
                                >+{{ pendingPositions.length }} org part{{
                                    pendingPositions.length > 1 ? 's' : ''
                                }}</span
                            >
                        </div>
                    </div>
                </mat-card-content>

                <mat-card-actions align="end" class="button-margin">
                    <button mat-stroked-button color="primary" matTooltip="Chat with user">
                        <mat-icon>chat</mat-icon> Chat
                    </button>
                    <button mat-stroked-button color="primary" matTooltip="Contact user">
                        <mat-icon>mail</mat-icon> Contact
                    </button>
                    <button
                        mat-stroked-button
                        color="primary"
                        (click)="openFullUserDetails()"
                        matTooltip="Open full staff member details dialog"
                    >
                        <mat-icon>person_search</mat-icon> Details
                    </button>
                </mat-card-actions>
            </mat-card>
        </div>

        <!-- ═══ Hats ═══ -->
        <mat-card class="mb-2">
            <mat-card-header class="collapsible-header" (click)="hatsSectionExpanded = !hatsSectionExpanded">
                <mat-icon mat-card-avatar class="section-icon hat-icon">school</mat-icon>
                <mat-card-title>
                    Hats ({{ userHats.length }})
                    <span *ngIf="pendingHats.length > 0" class="pending-count-chip" matTooltip="Pending approval"
                        >+{{ pendingHats.length }} pending</span
                    >
                </mat-card-title>
                <mat-icon class="expand-toggle">{{ hatsSectionExpanded ? 'expand_less' : 'expand_more' }}</mat-icon>
            </mat-card-header>
            <mat-card-content *ngIf="hatsSectionExpanded">
                <div *ngIf="userHats.length === 0" class="empty-msg">No hats assigned to this staff member</div>
                <div
                    *ngFor="let hat of userHats"
                    class="hat-row"
                    [class.hat-row-special]="hat.isSpecialRights"
                    [matTooltip]="hat.description || ''"
                >
                    <div class="hat-header">
                        <mat-icon class="hat-row-icon" [class.hat-row-icon-special]="hat.isSpecialRights">{{
                            hat.isSpecialRights ? 'shield' : 'school'
                        }}</mat-icon>
                        <span class="hat-name">{{ hat.name }}</span>
                        <span
                            *ngIf="hat.isSpecialRights"
                            class="sr-badge"
                            matTooltip="Special Rights — positionless hat granting org-wide elevated permissions"
                            >Special Rights</span
                        >
                        <span class="hat-users" *ngIf="hat.userCount > 1">· {{ hat.userCount }} staff</span>
                    </div>
                    <div *ngIf="hat.positionNames.length" class="hat-meta">
                        <span class="meta-label">Org Parts:</span> {{ hat.positionNames.join(', ') }}
                    </div>
                    <div *ngIf="hat.securityGroupNames.length" class="hat-sg-list">
                        <span class="sg-label">Security Groups:</span>
                        <span *ngFor="let sgName of hat.securityGroupNames" class="sg-chip">{{ sgName }}</span>
                    </div>
                </div>
                <!-- Pending Hats -->
                <div
                    *ngFor="let hat of pendingHats"
                    class="hat-row"
                    style="border: 1px dashed #ff9800; background: #fff8e1; border-radius: 6px; padding: 6px;"
                >
                    <div class="hat-header">
                        <mat-icon class="hat-row-icon" style="color: #ff9800;">hourglass_empty</mat-icon>
                        <span class="hat-name">{{ hat.name }}</span>
                        <span style="font-size: 10px; color: #ff9800; font-weight: 500; margin-left: 4px;"
                            >Pending</span
                        >
                    </div>
                    <div *ngIf="hat.positionNames.length" class="hat-meta">
                        <span class="meta-label">Org Parts:</span> {{ hat.positionNames.join(', ') }}
                    </div>
                </div>
            </mat-card-content>
        </mat-card>

        <!-- ═══ Positions ═══ -->
        <mat-card class="my-2">
            <mat-card-header class="collapsible-header" (click)="positionsSectionExpanded = !positionsSectionExpanded">
                <mat-icon mat-card-avatar class="section-icon">account_tree</mat-icon>
                <mat-card-title>
                    Org Parts ({{ userPositions.length }})
                    <span *ngIf="pendingPositions.length > 0" class="pending-count-chip" matTooltip="Pending approval"
                        >+{{ pendingPositions.length }} pending</span
                    >
                </mat-card-title>
                <mat-icon class="expand-toggle">{{
                    positionsSectionExpanded ? 'expand_less' : 'expand_more'
                }}</mat-icon>
            </mat-card-header>
            <mat-card-content *ngIf="positionsSectionExpanded">
                <div *ngIf="userPositions.length === 0 && pendingPositions.length === 0" class="empty-msg">
                    No org posts held by this staff member
                </div>
                <div *ngFor="let pos of userPositions" class="position-row">
                    <div class="position-header">
                        <mat-icon class="position-type-icon">{{ getPartTypeIcon(pos.partType) }}</mat-icon>
                        <strong>{{ pos.name }}</strong>
                    </div>
                    <div class="position-meta">
                        {{ pos.partType | titlecase }} · via <em>{{ pos.hatName }}</em>
                    </div>
                </div>
                <!-- Pending Positions -->
                <div
                    *ngFor="let pos of pendingPositions"
                    class="position-row position-row-pending"
                    matTooltip="Awaiting approval — not yet active"
                >
                    <div class="position-header">
                        <mat-icon class="position-type-icon" style="color: #ff9800;">hourglass_empty</mat-icon>
                        <strong>{{ pos.name }}</strong>
                        <span class="pending-inline-chip">Pending</span>
                    </div>
                    <div class="position-meta">
                        {{ pos.partType || 'post' | titlecase }} · via <em>{{ pos.hatName }}</em>
                    </div>
                </div>
            </mat-card-content>
        </mat-card>

        <!-- ═══ Security Groups & Permissions ═══ -->
        <mat-card class="my-2">
            <mat-card-header class="collapsible-header" (click)="sgSectionExpanded = !sgSectionExpanded">
                <mat-icon mat-card-avatar class="section-icon sg-icon-header">lock</mat-icon>
                <mat-card-title>Security Groups ({{ userSecurityGroups.length }})</mat-card-title>
                <mat-icon class="expand-toggle">{{ sgSectionExpanded ? 'expand_less' : 'expand_more' }}</mat-icon>
            </mat-card-header>
            <mat-card-content *ngIf="sgSectionExpanded">
                <div *ngIf="userSecurityGroups.length === 0" class="empty-msg">No security groups assigned</div>
                <div *ngFor="let sg of userSecurityGroups" class="sg-section">
                    <div class="sg-name-row" [matTooltip]="sg.description || ''">
                        <mat-icon class="sg-row-icon">lock</mat-icon>
                        <strong>{{ sg.name | normalize }}</strong>
                    </div>
                    <div *ngIf="sg.viaHats.length" class="sg-via-hats">via {{ sg.viaHats.join(', ') }}</div>
                    <div *ngFor="let feature of sg.features" class="feature-block">
                        <div class="feature-name" [matTooltip]="sg.featureDescriptions[feature] || ''">
                            <mat-icon class="feature-icon">star</mat-icon>
                            {{ feature | normalize }}
                        </div>
                        <div class="permission-list" *ngIf="sg.featureDetails[feature]?.permissions?.length">
                            <span
                                *ngFor="let perm of sg.featureDetails[feature].permissions"
                                class="perm-chip"
                                [matTooltip]="sg.permissionDescriptions[feature]?.[perm] || ''"
                            >
                                {{ perm | normalize }}
                            </span>
                        </div>
                    </div>
                </div>
            </mat-card-content>
        </mat-card>

        <!-- ═══ Policies ═══ -->
        <mat-card class="my-2">
            <mat-card-header class="collapsible-header" (click)="policiesSectionExpanded = !policiesSectionExpanded">
                <mat-icon mat-card-avatar class="section-icon policy-icon">policy</mat-icon>
                <mat-card-title>Policies ({{ userPolicies.length }})</mat-card-title>
                <mat-icon class="expand-toggle">{{ policiesSectionExpanded ? 'expand_less' : 'expand_more' }}</mat-icon>
            </mat-card-header>
            <mat-card-content *ngIf="policiesSectionExpanded">
                <div *ngIf="userPolicies.length === 0" class="empty-msg">No policies on this staff member</div>
                <div *ngFor="let policy of userPolicies" class="policy-row">
                    <div class="policy-name-row" [matTooltip]="policy.description || ''">
                        <mat-icon class="policy-row-icon">policy</mat-icon>
                        <strong>{{ policy.name }}</strong>
                    </div>
                    <div class="policy-meta">
                        <span
                            *ngIf="policy.category"
                            class="policy-chip category-chip"
                            [matTooltip]="'Category: ' + policy.category"
                            >{{ policy.category }}</span
                        >
                        <span
                            *ngIf="policy.status"
                            class="policy-chip status-chip"
                            [matTooltip]="'Status: ' + policy.status"
                            >{{ policy.status }}</span
                        >
                        <span class="policy-hat-label">via {{ policy.hatName }}</span>
                    </div>
                </div>
            </mat-card-content>
        </mat-card>

        <!-- ═══ Dev Details ═══ -->
        <mat-card class="my-2" *ngIf="isDev">
            <mat-card-header class="collapsible-header" (click)="devSectionExpanded = !devSectionExpanded">
                <mat-icon mat-card-avatar class="section-icon">code</mat-icon>
                <mat-card-title>Dev Details</mat-card-title>
                <mat-icon class="expand-toggle">{{ devSectionExpanded ? 'expand_less' : 'expand_more' }}</mat-icon>
            </mat-card-header>
            <mat-card-content *ngIf="devSectionExpanded" class="dev-details">
                <div class="dev-row">
                    <span class="dev-label">User Key:</span><span class="dev-value">{{ userKey }}</span>
                </div>
                <div class="dev-row">
                    <span class="dev-label">User UID:</span
                    ><span class="dev-value">{{ orgUserObj?.uid || 'N/A' }}</span>
                </div>
                <div class="dev-row">
                    <span class="dev-label">Hat Keys:</span
                    ><span class="dev-value">{{ orgUserObj?.hats?.join(', ') || 'none' }}</span>
                </div>
                <div class="dev-row">
                    <span class="dev-label">Is HFA:</span><span class="dev-value">{{ isHFA }}</span>
                </div>
            </mat-card-content>
        </mat-card>
    `,
    styles: [
        `
            :host {
                /* Plain block. The scrolling is owned by the ancestor
               .side-bar-content (configured in app.component.scss), so this
               component just lays out its children in normal flow. The
               sticky header below scopes to .side-bar-content's scroll. */
                display: block;
                width: 100%;
                box-sizing: border-box;
            }

            /* Wrapper owns the sticky behaviour AND the visual margin around
           the user-header card. The 0.5rem padding (replacing the card's
           previous my-2 margin) becomes part of the sticky element itself,
           so the grey gap above and below the card sticks WITH the card
           and masks any content that scrolls beneath. */
            .sticky-header-wrapper {
                position: -webkit-sticky;
                position: sticky;
                top: 0;
                z-index: 10;
                padding: 0.5rem 0;
                background: #ececec;
            }

            // --- User Header ---
            .position-header-card {
                .user-avatar-header {
                    border-radius: 50%;
                    overflow: hidden;
                    width: 48px;
                    height: 48px;

                    img {
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                    }
                }
            }

            .quick-stats {
                display: flex;
                gap: 16px;
                flex-wrap: wrap;
                justify-content: flex-end;
            }

            .quick-stat {
                text-align: center;

                strong {
                    display: block;
                    font-size: 1.2em;
                }
                small {
                    opacity: 0.7;
                }
            }

            .button-margin button {
                margin-left: 6px;
            }

            // --- Section headers ---
            .section-icon {
                background: rgba(255, 255, 255, 0.08);
                border-radius: 50%;
                padding: 6px;
                width: 36px;
                height: 36px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 20px;
            }

            .hat-icon {
                color: var(--entity-color-hats, #ff9800);
            }
            .sg-icon-header {
                color: var(--entity-color-security-groups, #66bb6a);
            }
            .policy-icon {
                color: var(--entity-color-policies, #42a5f5);
            }

            .collapsible-header {
                cursor: pointer;
                user-select: none;
                transition: background 0.15s;

                &:hover {
                    background: rgba(255, 255, 255, 0.04);
                    border-radius: 4px;
                }
            }

            .expand-toggle {
                margin-left: auto;
                opacity: 0.5;
            }

            // --- Hats Section ---
            .hat-row {
                padding: 8px 4px;
                border-bottom: 1px solid rgba(255, 255, 255, 0.06);
                &:last-child {
                    border-bottom: none;
                }
            }

            .hat-row-special {
                background: rgba(156, 39, 176, 0.1);
                border-left: 3px solid #9c27b0;
                border-radius: 4px;
                padding-left: 6px;
            }

            .hat-row-icon-special {
                color: #ce93d8 !important;
            }

            .sr-badge {
                /* Match .sr-header-chip sizing exactly, and push to the
               right edge of the hat row via margin-left:auto so it sits
               flush right within the flex .hat-header. */
                display: inline-flex;
                align-items: center;
                font-size: 10px;
                line-height: 14px;
                font-weight: 600;
                letter-spacing: 0.3px;
                padding: 0 8px;
                border-radius: 10px;
                margin-left: auto;
                color: #ce93d8;
                background: rgba(156, 39, 176, 0.18);
                border: 1px solid rgba(156, 39, 176, 0.45);
                text-transform: uppercase;
            }

            .sr-header-chip {
                display: inline-flex;
                align-items: center;
                gap: 3px;
                font-size: 10px;
                line-height: 14px;
                font-weight: 600;
                letter-spacing: 0.3px;
                padding: 0 8px;
                border-radius: 10px;
                margin-top: 4px;
                vertical-align: middle;
                color: #ce93d8;
                background: rgba(156, 39, 176, 0.18);
                border: 1px solid rgba(156, 39, 176, 0.45);
                text-transform: uppercase;

                mat-icon {
                    font-size: 12px;
                    width: 12px;
                    height: 12px;
                }
            }

            .hat-header {
                display: flex;
                align-items: center;
                gap: 6px;
            }

            .hat-row-icon {
                font-size: 18px;
                width: 18px;
                height: 18px;
                color: var(--entity-color-hats, #ff9800);
            }

            .hat-name {
                font-weight: 500;
                font-size: 14px;
            }

            .hat-users {
                font-size: 12px;
                color: rgba(255, 255, 255, 0.5);
            }

            .hat-meta {
                font-size: 12px;
                color: rgba(255, 255, 255, 0.6);
                margin-top: 3px;
                padding-left: 24px;

                .meta-label {
                    color: rgba(255, 255, 255, 0.45);
                }
            }

            .hat-sg-list {
                margin-top: 4px;
                padding-left: 24px;
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                gap: 4px;

                .sg-label {
                    font-size: 11px;
                    color: rgba(255, 255, 255, 0.45);
                    margin-right: 2px;
                }

                .sg-chip {
                    display: inline-block;
                    font-size: 11px;
                    padding: 1px 8px;
                    border-radius: 10px;
                    background: color-mix(in srgb, var(--entity-color-security-groups, #4caf50) 15%, transparent);
                    border: 1px solid color-mix(in srgb, var(--entity-color-security-groups, #4caf50) 30%, transparent);
                    color: var(--entity-color-security-groups, #a5d6a7);
                }
            }

            // --- Positions Section ---
            .position-row {
                padding: 8px 4px;
                border-bottom: 1px solid rgba(255, 255, 255, 0.06);
                &:last-child {
                    border-bottom: none;
                }
            }

            .position-row-pending {
                border: 1px dashed #ff9800;
                background: #fff8e1;
                border-radius: 6px;
                padding: 6px;
                margin-top: 4px;

                strong {
                    color: #5d4037;
                }
                .position-meta {
                    color: rgba(93, 64, 55, 0.7);
                }
            }

            .pending-inline-chip {
                font-size: 10px;
                color: #ff9800;
                font-weight: 600;
                margin-left: 4px;
                text-transform: uppercase;
                letter-spacing: 0.3px;
            }

            .pending-count-chip {
                display: inline-flex;
                align-items: center;
                font-size: 10px;
                font-weight: 600;
                letter-spacing: 0.3px;
                padding: 1px 7px;
                border-radius: 10px;
                margin-left: 6px;
                color: #ff9800;
                background: rgba(255, 152, 0, 0.18);
                border: 1px solid rgba(255, 152, 0, 0.45);
                text-transform: uppercase;
                vertical-align: middle;
            }

            .pending-banner {
                margin-top: 10px;
                padding: 8px 10px;
                border: 1px dashed #ff9800;
                background: #fff8e1;
                border-radius: 6px;
                color: #5d4037;
                cursor: help;
            }

            .pending-banner-header {
                display: flex;
                align-items: center;
                gap: 6px;
                font-weight: 600;
                font-size: 12px;
                color: #ef6c00;
                text-transform: uppercase;
                letter-spacing: 0.4px;
            }

            .pending-banner-icon {
                font-size: 16px;
                width: 16px;
                height: 16px;
                color: #ef6c00;
            }

            .pending-banner-title {
                font-size: 11px;
            }

            .pending-banner-body {
                margin-top: 4px;
                display: flex;
                flex-wrap: wrap;
                gap: 8px;
                font-size: 12px;
            }

            .pending-banner-line {
                background: rgba(255, 152, 0, 0.2);
                color: #5d4037;
                padding: 1px 8px;
                border-radius: 10px;
                font-weight: 500;
            }

            .position-header {
                display: flex;
                align-items: center;
                gap: 6px;
            }

            .position-type-icon {
                font-size: 18px;
                width: 18px;
                height: 18px;
                color: var(--entity-color-positions, #4fc3f7);
            }

            .position-meta {
                font-size: 12px;
                color: rgba(255, 255, 255, 0.5);
                padding-left: 24px;
                margin-top: 2px;
            }

            // --- SG / Features / Permissions ---
            .sg-section {
                padding: 8px 0;
                border-bottom: 1px solid rgba(255, 255, 255, 0.06);
                &:last-child {
                    border-bottom: none;
                }
            }

            .sg-name-row {
                display: flex;
                align-items: center;
                gap: 6px;
                margin-bottom: 4px;
            }

            .sg-row-icon {
                font-size: 16px;
                width: 16px;
                height: 16px;
                color: var(--entity-color-security-groups, #66bb6a);
            }

            .sg-via-hats {
                font-size: 12px;
                color: rgba(255, 255, 255, 0.45);
                padding-left: 22px;
                margin-bottom: 4px;
                font-style: italic;
            }

            .feature-block {
                margin: 4px 0 4px 22px;
            }

            .feature-name {
                display: flex;
                align-items: center;
                gap: 4px;
                font-size: 13px;
                font-weight: 500;
            }

            .feature-icon {
                font-size: 14px;
                width: 14px;
                height: 14px;
                color: var(--entity-color-features, #ffd54f);
            }

            .permission-list {
                display: flex;
                flex-wrap: wrap;
                gap: 4px;
                padding: 3px 0 3px 18px;
            }

            .perm-chip {
                display: inline-block;
                font-size: 11px;
                padding: 1px 7px;
                border-radius: 10px;
                background: rgba(255, 255, 255, 0.06);
                border: 1px solid rgba(255, 255, 255, 0.1);
                color: rgba(255, 255, 255, 0.65);
            }

            // --- Policies Section ---
            .policy-row {
                padding: 8px 0;
                border-bottom: 1px solid rgba(255, 255, 255, 0.06);
                &:last-child {
                    border-bottom: none;
                }
            }

            .policy-name-row {
                display: flex;
                align-items: center;
                gap: 6px;
                margin-bottom: 4px;
            }

            .policy-row-icon {
                font-size: 16px;
                width: 16px;
                height: 16px;
                color: var(--entity-color-policies, #42a5f5);
            }

            .policy-meta {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                gap: 6px;
                padding-left: 22px;
            }

            .policy-chip {
                display: inline-block;
                font-size: 11px;
                padding: 1px 8px;
                border-radius: 10px;
            }

            .category-chip {
                background: color-mix(in srgb, var(--entity-color-policies, #42a5f5) 15%, transparent);
                border: 1px solid color-mix(in srgb, var(--entity-color-policies, #42a5f5) 30%, transparent);
                color: var(--entity-color-policies, #90caf9);
            }

            .status-chip {
                background: rgba(255, 183, 77, 0.15);
                border: 1px solid rgba(255, 183, 77, 0.3);
                color: #ffcc80;
            }

            .policy-hat-label {
                font-size: 11px;
                color: rgba(255, 255, 255, 0.4);
                font-style: italic;
            }

            // --- Dev Details ---
            .dev-details {
                .dev-row {
                    display: flex;
                    padding: 3px 0;
                    font-size: 12px;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.04);
                }
                .dev-label {
                    width: 80px;
                    color: rgba(255, 255, 255, 0.5);
                    flex-shrink: 0;
                }
                .dev-value {
                    word-break: break-all;
                    color: rgba(255, 255, 255, 0.8);
                }
            }

            // --- Empty ---
            .empty-msg {
                font-size: 13px;
                color: rgba(255, 255, 255, 0.4);
                font-style: italic;
                padding: 8px 0;
            }

            // ═══ Light theme overrides ═══
            :host-context(:not(.dark-theme)) {
                .quick-stat small {
                    opacity: 0.6;
                }

                .section-icon {
                    background: rgba(0, 0, 0, 0.06);
                }

                .collapsible-header:hover {
                    background: rgba(0, 0, 0, 0.04);
                }

                .hat-row {
                    border-color: rgba(0, 0, 0, 0.08);
                }
                .hat-users {
                    color: rgba(0, 0, 0, 0.54);
                }

                .hat-meta {
                    color: rgba(0, 0, 0, 0.54);
                    .meta-label {
                        color: rgba(0, 0, 0, 0.5);
                    }
                }

                .hat-sg-list {
                    .sg-label {
                        color: rgba(0, 0, 0, 0.5);
                    }
                    .sg-chip {
                        background: color-mix(in srgb, var(--entity-color-security-groups, #4caf50) 10%, transparent);
                        border-color: color-mix(in srgb, var(--entity-color-security-groups, #4caf50) 35%, transparent);
                        color: var(--entity-color-security-groups, #2e7d32);
                    }
                }

                .position-row {
                    border-color: rgba(0, 0, 0, 0.08);
                }
                .position-meta {
                    color: rgba(0, 0, 0, 0.54);
                }

                .sg-section {
                    border-color: rgba(0, 0, 0, 0.08);
                }
                .sg-via-hats {
                    color: rgba(0, 0, 0, 0.5);
                }

                .perm-chip {
                    background: rgba(0, 0, 0, 0.05);
                    border-color: rgba(0, 0, 0, 0.12);
                    color: rgba(0, 0, 0, 0.65);
                }

                .policy-row {
                    border-color: rgba(0, 0, 0, 0.08);
                }

                .category-chip {
                    background: color-mix(in srgb, var(--entity-color-policies, #1976d2) 8%, transparent);
                    border-color: color-mix(in srgb, var(--entity-color-policies, #1976d2) 30%, transparent);
                    color: var(--entity-color-policies, #1565c0);
                }

                .status-chip {
                    background: rgba(255, 152, 0, 0.08);
                    border-color: rgba(255, 152, 0, 0.3);
                    color: #e65100;
                }

                .policy-hat-label {
                    color: rgba(0, 0, 0, 0.45);
                }

                .empty-msg {
                    color: rgba(0, 0, 0, 0.45);
                }

                .dev-details {
                    .dev-row {
                        border-color: rgba(0, 0, 0, 0.06);
                    }
                    .dev-label {
                        color: rgba(0, 0, 0, 0.54);
                    }
                    .dev-value {
                        color: rgba(0, 0, 0, 0.87);
                    }
                }
            }
        `,
    ],
})
export class SidebarUserComponent extends OrbBoardBaseComponent {
    @Input() public isDev = false;
    @Input() public isEditing = false;
    @Input() public node!: ECONode<Position>;
    @Input() public user!: UserDetails;
    /** The org user key — can be provided directly for HFA users */
    @Input() public userKey = '';
    /** Whether this user is HFA on the current node */
    @Input() public isHFA = false;

    public obService = inject(OrgBoardService);
    private sidebarService = inject(RightSideBarService);
    private profileColorService = inject(ProfileColorService);

    public userName = '';
    public userEmail = '';
    public orgUserObj: OrgUser | null = null;
    public userHats: UserHatInfo[] = [];
    public pendingHats: UserHatInfo[] = [];
    public userPositions: UserPositionInfo[] = [];
    public pendingPositions: UserPositionInfo[] = [];
    public userSecurityGroups: UserSecurityGroupInfo[] = [];
    public userPolicies: UserPolicyInfo[] = [];

    /** True when the user holds at least one special-rights (positionless / org-wide elevated) hat. */
    public get hasSpecialRights(): boolean {
        return this.userHats.some((h) => h.isSpecialRights);
    }

    // Collapse state (only Hats expanded by default)
    public hatsSectionExpanded = true;
    public positionsSectionExpanded = false;
    public sgSectionExpanded = false;
    public policiesSectionExpanded = false;
    public devSectionExpanded = false;

    public override ngOnInit(): void {
        this.resolveData();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['user'] || changes['userKey'] || changes['org']) {
            this.resolveData();
        }
    }

    private resolveData(): void {
        const org = this.org;
        if (!org) return;

        // Resolve user key
        const key = this.userKey || this.user?.key || '';
        if (!key) return;

        // Ensure profile color is loaded for this user
        this.profileColorService.ensureLoaded(key);

        // Get OrgUser from organization
        this.orgUserObj = (org.orgUsers?.[key] as OrgUser) || null;
        this.userName = this.user?.name || this.orgUserObj?.name || this.orgUserObj?.userName || key;
        this.userEmail = this.orgUserObj?.email || '';

        // Resolve hats for this user
        this.userHats = this.resolveHats(key, org);

        // Resolve pending hats for this user
        this.pendingHats = this.resolvePendingHats(key, org);

        // Resolve positions from hats
        this.userPositions = this.resolvePositions(key, org);

        // Resolve pending positions from pending hats (positions user WOULD hold once approved)
        this.pendingPositions = this.resolvePendingPositions(key, org);

        // Resolve security groups from hats
        this.userSecurityGroups = this.resolveSecurityGroups(key, org);

        // Resolve policies from hats
        this.userPolicies = this.resolvePolicies(key, org);
    }

    private resolveHats(userKey: string, org: Organization): UserHatInfo[] {
        const hatKeys = org.orgHats?.hatsKeys || [];
        const result: UserHatInfo[] = [];

        for (const hk of hatKeys) {
            const hat = org.orgHats?.[hk] as OrgHat;
            if (!hat || hat.isSoftDeleted) continue;

            const assignedUsers = hat.assignedToArray || hat.userKeys || [];
            if (!assignedUsers.includes(userKey)) continue;

            // Resolve position names
            const posNames = (hat.positionArray || []).map((pk) => {
                const part = org.orgParts?.find((p) => p.partKey === pk || p.key === pk);
                return part?.name || pk;
            });

            // Resolve SG names
            const sgKeys = (hat.securityGroupsArray || hat.securityGroups || []).map((sg: string | { key: string }) =>
                typeof sg === 'string' ? sg : sg.key
            );
            const sgNames = sgKeys
                .filter((k: string) => org.orgSecurityGroups?.[k])
                .map((k: string) => org.orgSecurityGroups[k].name || k);

            result.push({
                key: hat.key,
                name: hat.name,
                description: hat.description || '',
                positionNames: posNames,
                securityGroupNames: sgNames,
                userCount: assignedUsers.length,
                isSpecialRights:
                    !!(hat as OrgHat & { isSpecialRights?: boolean }).isSpecialRights ||
                    !hat.positionArray ||
                    hat.positionArray.length === 0,
            });
        }
        return result;
    }

    private resolvePendingHats(userKey: string, org: Organization): UserHatInfo[] {
        const hatKeys = org.orgHats?.hatsKeys || [];
        const result: UserHatInfo[] = [];

        for (const hk of hatKeys) {
            const hat = org.orgHats?.[hk] as OrgHat;
            if (!hat || hat.isSoftDeleted) continue;

            const pendingUsers = hat.pendingAssignedToArray || [];
            if (!pendingUsers.includes(userKey)) continue;

            // Skip if already active
            const activeUsers = hat.assignedToArray || hat.userKeys || [];
            if (activeUsers.includes(userKey)) continue;

            const posNames = (hat.positionArray || []).map((pk) => {
                const part = org.orgParts?.find((p) => p.partKey === pk || p.key === pk);
                return part?.name || pk;
            });

            result.push({
                key: hat.key,
                name: hat.name,
                description: hat.description || '',
                positionNames: posNames,
                securityGroupNames: [],
                userCount: activeUsers.length,
                isSpecialRights:
                    !!(hat as OrgHat & { isSpecialRights?: boolean }).isSpecialRights ||
                    !hat.positionArray ||
                    hat.positionArray.length === 0,
            });
        }
        return result;
    }

    private resolvePositions(userKey: string, org: Organization): UserPositionInfo[] {
        const result: UserPositionInfo[] = [];
        const seen = new Set<string>();

        for (const hat of this.userHats) {
            const hatObj = org.orgHats?.[hat.key] as OrgHat;
            if (!hatObj) continue;

            for (const pk of hatObj.positionArray || []) {
                if (seen.has(pk)) continue;
                seen.add(pk);
                const part = org.orgParts?.find((p) => p.partKey === pk || p.key === pk);
                result.push({
                    partKey: pk,
                    name: part?.name || pk,
                    partType: part?.partType || 'position',
                    hatName: hat.name,
                });
            }
        }
        return result;
    }

    /**
     * Resolve positions the user would hold once their pending hat assignments are approved.
     * Skips positions already covered by an active (approved) hat — only purely-pending posts
     * surface here, which matches the orange "+1 org parts" preview from the full user dialog.
     */
    private resolvePendingPositions(userKey: string, org: Organization): UserPositionInfo[] {
        const result: UserPositionInfo[] = [];
        const activeKeys = new Set(this.userPositions.map((p) => p.partKey));
        const seen = new Set<string>();

        for (const hat of this.pendingHats) {
            const hatObj = org.orgHats?.[hat.key] as OrgHat;
            if (!hatObj) continue;

            for (const pk of hatObj.positionArray || []) {
                if (!pk || seen.has(pk) || activeKeys.has(pk)) continue;
                seen.add(pk);
                const part = org.orgParts?.find((p) => p.partKey === pk || p.key === pk);
                result.push({
                    partKey: pk,
                    name: part?.name || pk,
                    partType: part?.partType || 'post',
                    hatName: hat.name,
                });
            }
        }
        return result;
    }

    private resolveSecurityGroups(userKey: string, org: Organization): UserSecurityGroupInfo[] {
        const seen = new Set<string>();
        const result: UserSecurityGroupInfo[] = [];

        for (const hat of this.userHats) {
            const hatObj = org.orgHats?.[hat.key] as OrgHat;
            if (!hatObj) continue;

            const sgEntries = hatObj.securityGroupsArray || hatObj.securityGroups || [];
            for (const entry of sgEntries) {
                const sgKey = typeof entry === 'string' ? entry : (entry as { key: string }).key;
                if (!sgKey) continue;

                const orgSg = org.orgSecurityGroups?.[sgKey];
                if (!orgSg) continue;

                if (seen.has(sgKey)) {
                    // Add hat name to existing SG entry
                    const existing = result.find((r) => r.key === sgKey);
                    if (existing && !existing.viaHats.includes(hat.name)) {
                        existing.viaHats.push(hat.name);
                    }
                    continue;
                }
                seen.add(sgKey);

                const features = orgSg.features || [];
                const featureDescriptions: Record<string, string> = {};
                const permissionDescriptions: Record<string, Record<string, string>> = {};
                for (const fKey of features) {
                    const orgFeature = org.orgFeatures?.[fKey];
                    if (orgFeature?.description) featureDescriptions[fKey] = orgFeature.description;
                    if (orgFeature?.permissionDescriptions)
                        permissionDescriptions[fKey] = orgFeature.permissionDescriptions;
                }

                result.push({
                    key: sgKey,
                    name: orgSg.name || sgKey,
                    description: (orgSg as typeof orgSg & { description?: string }).description,
                    features,
                    featureDetails: orgSg.featureDetails || {},
                    featureDescriptions,
                    permissionDescriptions,
                    viaHats: [hat.name],
                });
            }
        }
        return result;
    }

    private resolvePolicies(userKey: string, org: Organization): UserPolicyInfo[] {
        const result: UserPolicyInfo[] = [];
        const orgPolicies = org.orgPolicies as Record<string, any> | undefined;
        if (!orgPolicies) return result;

        const orgHats = org.orgHats || {};
        const userHatKeys = this.userHats.map((h) => h.key);

        // Iterate all org policies and check if they target this user
        const policyKeys = (orgPolicies['policiesKeys'] as string[]) || [];
        for (const policyKey of policyKeys) {
            if (!policyKey) continue;
            const policy = orgPolicies[policyKey] as any;
            if (!policy) continue;

            // Only include published policies
            if (policy.status !== 'published') continue;

            const targets = policy.targets as any;
            if (!targets) continue;

            // Check if user is directly in affectedUserUids
            const affectedUids: string[] = targets.affectedUserUids || [];
            const isDirectlyAffected = affectedUids.includes(userKey);

            // Check which of the user's hats are in resolvedHatKeys
            const resolvedHatKeys: string[] = targets.resolvedHatKeys || [];
            const matchingHatKeys = userHatKeys.filter((hk) => resolvedHatKeys.includes(hk));

            if (!isDirectlyAffected && matchingHatKeys.length === 0) continue;

            // Build "via" label from matching hats
            const viaHatNames = matchingHatKeys.map((hk) => (orgHats[hk] as OrgHat)?.name || hk).filter(Boolean);

            result.push({
                key: policyKey,
                name: (policy.name as string) || policyKey,
                description: (policy.description as string) || '',
                category: (policy.category as string) || '',
                status: (policy.status as string) || '',
                hatName: viaHatNames.join(', ') || (isDirectlyAffected ? 'Direct assignment' : ''),
            });
        }
        return result;
    }

    public getUserAvatarUrl(): string {
        if (this.user?.photoThumbnail) return this.user.photoThumbnail;
        if (this.orgUserObj?.photoThumbnail) return this.orgUserObj.photoThumbnail;
        const key = this.userKey || this.user?.key || '';
        const name = this.userName || 'User';
        return this.profileColorService.getAvatarUrl(key, name, 48);
    }

    public getPartTypeIcon(partType: string): string {
        switch (partType) {
            case 'department':
                return 'business';
            case 'division':
                return 'domain';
            case 'section':
                return 'folder';
            case 'unit':
                return 'workspaces';
            case 'post':
                return 'badge';
            default:
                return 'account_tree';
        }
    }

    public openFullUserDetails(): void {
        if (this.node && this.user) {
            this.dialogService?.openOrgUserDetailsDialog(this.node, this.user);
        }
    }

    public get positions(): Position[] {
        return this.user?.positions || [];
    }

    public get positionTitles(): string {
        return this.positions?.map((p) => p.name)?.join(', ') || '';
    }
}
